// How the web app and the phone app call the platform (TF-269): the token, the company, JSON in and
// out, and the platform's errors as ApiError. What differs between the two apps - where the token
// comes from, what signing in again means, which headers say who is calling - is the config.

/** A platform API answered with an error: status, machine-readable code, human-readable message. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** The input the error is about, as the API names it: a form shows the error at that field (TF-91). */
    readonly field?: string,
    /** Whatever else the answer said: e.g. why and until when a company is read-only (TF-113). */
    readonly details: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

export const isApiError = (error: unknown, status?: number): error is ApiError =>
  error instanceof ApiError && (status === undefined || error.status === status);

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  /** Sent as JSON; a Blob (an image, TF-310) is sent as it is, under its own type. */
  body?: unknown;
  /** The company whose data is accessed (X-Tenant-Id). */
  tenantId?: string | null;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Public routes (shared links): no token. */
  anonymous?: boolean;
}

/** One platform call: the parsed JSON body, undefined for 204. */
export type Call = <T>(path: string, options?: RequestOptions) => Promise<T>;

export interface ClientConfig {
  /** Where the platform is: '' for the web app (the same origin), the server's address for the phone app. */
  baseUrl?: string;
  /** The person's access token; null when they are signed out. */
  token: () => Promise<string | null>;
  /** Called when there is no token, before the call fails with SIGNED_OUT: the app starts signing in. */
  signedOut: () => unknown;
  /** Whether the platform answered (TF-133: the web app offers the status page when it does not). */
  reachable?: (path: string, ok: boolean) => void;
  /** Sent with every call: the phone app says which app it is (X-DoCuLat-App, ADR 0183). */
  headers?: () => Record<string, string>;
  /** The fetch to use; the global one when left out (tests pass their own). */
  fetch?: typeof fetch;
}

export interface Client {
  /** Calls a platform API. Resolves to the parsed JSON body (undefined for 204). */
  call: Call;
  /** A signed-in GET whose answer is not JSON (a download, an image): the response, or ApiError. */
  get: (path: string, options?: Pick<RequestOptions, 'tenantId' | 'headers' | 'signal'>) => Promise<Response>;
}

/** The gateway's answers when the service behind it is down or not answering in time. */
const GATEWAY_DOWN = new Set([502, 503, 504]);

export function createClient(config: ClientConfig): Client {
  const base = (config.baseUrl ?? '').replace(/\/+$/, '');
  const send = (path: string, init: RequestInit) => (config.fetch ?? fetch)(base + path, init);
  const reachable = config.reachable ?? (() => {});

  async function headersFor(options: RequestOptions): Promise<Record<string, string>> {
    const headers: Record<string, string> = { ...config.headers?.(), ...options.headers };
    if (!options.anonymous) {
      const token = await config.token();
      if (!token) {
        await config.signedOut();
        throw new ApiError(401, 'SIGNED_OUT', 'Please sign in again');
      }
      headers.authorization = `Bearer ${token}`;
    }
    if (options.tenantId) headers['x-tenant-id'] = options.tenantId;
    return headers;
  }

  const call: Call = async <T>(path: string, options: RequestOptions = {}): Promise<T> => {
    const headers = await headersFor(options);
    const raw = typeof Blob !== 'undefined' && options.body instanceof Blob ? options.body : null;
    if (options.body !== undefined) headers['content-type'] = raw ? raw.type || 'application/octet-stream' : 'application/json';

    let response: Response;
    try {
      response = await send(path, {
        method: options.method ?? 'GET',
        headers,
        body: options.body === undefined ? undefined : (raw ?? JSON.stringify(options.body)),
        signal: options.signal,
      });
    } catch (error) {
      // Stopped on purpose (a page left, a search typed further): not a sign of anything.
      if (options.signal?.aborted) throw error;
      // Never reached the platform: no network, or nothing answering.
      reachable(path, false);
      throw new ApiError(0, 'UNREACHABLE', 'DoCuLat cannot be reached');
    }
    if (response.status === 204) {
      reachable(path, true);
      return undefined as T;
    }
    const text = await response.text();
    const parsed: unknown = text ? safeJson(text) : undefined;
    // The gateway answering for a service that does not (no answer of the service's own, with its code):
    // the platform is not there either. A service's own 503 ("not set up yet") is an answer.
    reachable(path, !(GATEWAY_DOWN.has(response.status) && !(parsed as { code?: unknown } | undefined)?.code));
    if (!response.ok) {
      const problem = (parsed ?? {}) as { code?: string; message?: string; field?: string } & Record<string, unknown>;
      throw new ApiError(response.status, problem.code ?? `HTTP_${response.status}`, problem.message ?? response.statusText, problem.field, problem);
    }
    return parsed as T;
  };

  const get: Client['get'] = async (path, options = {}) => {
    const response = await send(path, { method: 'GET', headers: await headersFor(options), signal: options.signal });
    if (!response.ok) {
      const problem = (safeJson(await response.text()) ?? {}) as { code?: string; message?: string };
      throw new ApiError(response.status, problem.code ?? `HTTP_${response.status}`, problem.message ?? response.statusText);
    }
    return response;
  };

  return { call, get };
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

/** A query string from the values that are set: `?a=1&b=x`, or '' when none is. */
export function query(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  const text = search.toString();
  return text ? `?${text}` : '';
}
